import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const articles = [
  {
    title: "The Invisible Interface: Designing for Feel",
    date: "Mar 2025",
    topic: "Design",
    excerpt: "Why the best digital experiences are the ones you don't consciously notice.",
  },
  {
    title: "Film Grain in the Digital Age",
    date: "Jan 2025",
    topic: "Aesthetics",
    excerpt: "Exploring the resurgence of analog textures in modern web design.",
  },
  {
    title: "Motion as Meaning: Beyond Decoration",
    date: "Nov 2024",
    topic: "Motion",
    excerpt: "How purposeful animation can communicate hierarchy, state, and narrative.",
  },
  {
    title: "Typography Systems for the Web",
    date: "Sep 2024",
    topic: "Typography",
    excerpt: "Building flexible, expressive type scales that work across every breakpoint.",
  },
];

export default function Articles() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-articles-heading]",
        { opacity: 0, y: 30 },
        {
          opacity: 1, y: 0, duration: 0.8, ease: "power3.out",
          scrollTrigger: { trigger: sectionRef.current, start: "top 80%" },
        }
      );

      gsap.utils.toArray("[data-article-item]").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 40 },
          {
            opacity: 1, y: 0, duration: 0.8, ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 90%" },
          }
        );
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="articles" className="px-6 py-16 md:px-12 md:py-24 border-t border-ink/10">
      <div data-articles-heading className="mb-12 md:mb-16">
        <span className="font-mono text-xs tracking-widest uppercase text-ink/40 block mb-3">
          Writing
        </span>
        <h2 className="font-serif text-4xl md:text-6xl font-bold">Articles</h2>
      </div>

      <div className="border-t border-ink/10">
        {articles.map((article, i) => (
          <a
            key={article.title}
            href="#"
            onClick={(e) => e.preventDefault()}
            data-article-item
            data-cursor-hover
            className="group block border-b border-ink/10 py-6 md:py-8 px-2 md:px-4 no-underline text-ink opacity-0"
          >
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2 md:gap-8">
              <div className="flex items-baseline gap-4 md:gap-8 flex-1">
                <span className="font-mono text-xs text-ink/30 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-xl md:text-2xl font-bold group-hover:text-ink/70 transition-colors duration-300">
                  {article.title}
                </h3>
              </div>
              <div className="flex items-baseline gap-4 md:gap-6 ml-8 md:ml-0 shrink-0">
                <span className="font-mono text-xs text-ink/40">{article.topic}</span>
                <span className="font-mono text-xs text-ink/30">{article.date}</span>
              </div>
            </div>
            <p className="mt-2 ml-8 md:ml-12 font-sans text-sm text-ink/40 leading-relaxed max-w-xl">
              {article.excerpt}
            </p>
          </a>
        ))}
      </div>
    </section>
  );
}
